/**
 * Dashboard page component
 */

import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui';
import { useTenants, useTenantStatistics } from '@/hooks';

export function Dashboard() {
  // Get tenant statistics for dashboard metrics
  const { data: stats, isLoading: statsLoading, error: statsError } = useTenantStatistics();
  
  // Get recent tenants for the recent tenants section
  const { data: paginatedTenants, isLoading: tenantsLoading, error: tenantsError } = useTenants({ limit: 5 });
  const recentTenants = paginatedTenants?.items || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h1 font-semibold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-2">Welcome to your tenant management dashboard</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">Total Tenants</h3>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {statsLoading ? '...' : (stats?.total || 0)}
            </div>
            <p className="text-sm text-gray-500 mt-1">All tenant accounts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">Active Tenants</h3>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-success">
              {statsLoading ? '...' : (stats?.active || 0)}
            </div>
            <p className="text-sm text-gray-500 mt-1">Currently active tenants</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">Trial Tenants</h3>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-warning">
              {statsLoading ? '...' : (stats?.trial || 0)}
            </div>
            <p className="text-sm text-gray-500 mt-1">Tenants on trial</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-medium text-gray-900">Recent Tenants</h3>
        </CardHeader>
        <CardContent>
          {tenantsLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading recent tenants...</p>
            </div>
          ) : tenantsError ? (
            <div className="text-center py-8">
              <p className="text-red-600">Error loading tenants</p>
              <p className="text-sm text-gray-500 mt-1">Unable to load recent tenant data</p>
            </div>
          ) : recentTenants && recentTenants.length > 0 ? (
            <div className="space-y-3">
              {recentTenants.map((tenant) => (
                <div key={tenant.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                  <div>
                    <h4 className="font-medium text-gray-900">{tenant.company}</h4>
                    <p className="text-sm text-gray-500">ID: {tenant.id}</p>
                  </div>
                  <div className="text-sm text-gray-500">
                    Status: {tenant.status}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No tenants found</p>
              <p className="text-sm text-gray-400 mt-1">Create your first tenant to get started</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
